import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, Calendar, MapPin, Users as UsersIcon, Mic, Building2, Mail, Phone } from "lucide-react";
import { PageHeader, SectionLabel } from "@/components/shared";
import { Reveal, FadeUp } from "@/components/motion/Reveal";
import Testimonials from "@/components/Testimonials";
import { postBooking } from "@/lib/api";
import { ASSETS } from "@/lib/assets";

const SERVICES = ["Preaching", "Workshops", "Training", "Worship Night", "Youth / Camp"];

const WHATS_PROVIDED = [
  { icon: Mic, title: "Preaching", body: "Gospel-centred, biblically grounded messages for Sunday services, conferences, and special events." },
  { icon: UsersIcon, title: "Workshops", body: "Interactive sessions on evangelism, prayer, and awakening the local Church." },
  { icon: Calendar, title: "Training", body: "Multi-day intensives that equip believers to make disciples and share the Gospel boldly." },
];

export default function Book() {
  const [form, setForm] = useState({
    organization: "", name: "", email: "", phone: "",
    event_date: "", location: "", services: [], audience_size: "", message: "",
  });
  const [loading, setLoading] = useState(false);

  const toggleService = (s) => setForm((f) => ({
    ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.organization || !form.name || !form.email || !form.event_date || !form.location) {
      return toast.error("Please fill in all required fields.");
    }
    setLoading(true);
    try {
      const res = await postBooking(form);
      toast.success(res.message || "Request sent!");
      setForm({ organization: "", name: "", email: "", phone: "", event_date: "", location: "", services: [], audience_size: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b border-white/20 focus:border-wutn py-3 text-white placeholder:text-zinc-500 outline-none transition-colors";

  return (
    <div data-testid="book-page">
      <PageHeader
        testId="book-header"
        label="Invite Kerby"
        title={<>Book Me To <span className="text-wutn">Speak</span></>}
        subtitle="Churches, camps, conferences, and schools — if God is stirring you to invite Kerby, send the details below. We'll be in touch."
        image={ASSETS.preachingPodium}
      />

      <section className="bg-black py-16 md:py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <SectionLabel className="mb-10">What I Bring</SectionLabel>
          <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10 mb-16">
            {WHATS_PROVIDED.map((w, i) => (
              <FadeUp key={w.title} delay={i * 0.06}>
                <div className="bg-black p-8 h-full">
                  <span className="inline-flex h-12 w-12 items-center justify-center bg-wutn/10 text-wutn"><w.icon className="h-6 w-6" /></span>
                  <h3 className="mt-5 font-display uppercase text-2xl">{w.title}</h3>
                  <p className="mt-3 text-zinc-400 text-sm">{w.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>

          <div className="grid lg:grid-cols-12 gap-14">
            <div className="lg:col-span-5">
              <SectionLabel>How It Works</SectionLabel>
              <h2 className="mt-5 font-display uppercase text-4xl md:text-5xl leading-[0.9]">Simple <span className="text-wutn">Process</span></h2>
              <ol className="mt-8 space-y-6">
                {[
                  ["01", "Submit your request", "Fill out the form with your event details and the ministry you'd like Kerby to serve."],
                  ["02", "We review & reply", "Our team will review and reach out within a few days to discuss availability and details."],
                  ["03", "Confirm & prepare", "Once confirmed, we'll coordinate travel, materials, and anything else your event needs."],
                ].map(([n, t, d]) => (
                  <li key={n} className="flex gap-5">
                    <span className="font-display text-4xl text-outline-red leading-none">{n}</span>
                    <div>
                      <h4 className="font-cond uppercase text-white text-lg">{t}</h4>
                      <p className="text-sm text-zinc-400 mt-1">{d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>

            <div className="lg:col-span-6 lg:col-start-7">
              <Reveal>
                <form onSubmit={submit} data-testid="booking-form" className="bg-[#0A0A0A] border border-white/10 p-8 md:p-10 space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-2"><Building2 className="h-3 w-3" /> Church / Organization *</label>
                      <input required value={form.organization} onChange={(e)=>setForm({...form,organization:e.target.value})} data-testid="book-org" className={inputCls} placeholder="e.g. Grace Community Church" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-zinc-500">Your Name *</label>
                      <input required value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid="book-name" className={inputCls} placeholder="Full name" />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-2"><Mail className="h-3 w-3" /> Email *</label>
                      <input type="email" required value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} data-testid="book-email" className={inputCls} placeholder="you@example.com" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-2"><Phone className="h-3 w-3" /> Phone</label>
                      <input value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} data-testid="book-phone" className={inputCls} placeholder="(optional)" />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-2"><Calendar className="h-3 w-3" /> Event Date *</label>
                      <input required value={form.event_date} onChange={(e)=>setForm({...form,event_date:e.target.value})} data-testid="book-date" className={inputCls} placeholder="e.g. June 14, 2026 or June 14–16, 2026" />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-2"><MapPin className="h-3 w-3" /> Location *</label>
                      <input required value={form.location} onChange={(e)=>setForm({...form,location:e.target.value})} data-testid="book-location" className={inputCls} placeholder="City, Province" />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-widest text-zinc-500 mb-3 block">Services Requested</label>
                    <div className="flex flex-wrap gap-2">
                      {SERVICES.map((s) => {
                        const on = form.services.includes(s);
                        return (
                          <button type="button" key={s} onClick={() => toggleService(s)} data-testid={`book-service-${s.toLowerCase().replace(/[^a-z0-9]/g,'-')}`}
                            className={`px-4 py-2 font-cond uppercase tracking-wider text-xs border transition-colors ${on ? "bg-wutn border-wutn text-white" : "border-white/20 text-zinc-400 hover:border-wutn hover:text-white"}`}>
                            {s}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-2"><UsersIcon className="h-3 w-3" /> Expected Audience Size</label>
                    <input value={form.audience_size} onChange={(e)=>setForm({...form,audience_size:e.target.value})} data-testid="book-audience" className={inputCls} placeholder="e.g. 150" />
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-widest text-zinc-500">Message / Additional Details</label>
                    <textarea rows={4} value={form.message} onChange={(e)=>setForm({...form,message:e.target.value})} data-testid="book-message" className={`${inputCls} resize-none`} placeholder="Share more about the event, theme, travel, or anything we should know." />
                  </div>

                  <button type="submit" disabled={loading} data-testid="book-submit" className="w-full inline-flex items-center justify-center gap-2 bg-wutn hover:bg-wutn-dark disabled:opacity-60 text-white px-6 py-4 font-cond uppercase tracking-wider text-sm transition-colors">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send Request
                  </button>
                  <p className="text-xs text-zinc-500 text-center">We respond to every request personally.</p>
                </form>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <Testimonials label="Trusted By" heading={<>Pastors Who&apos;ve <span className="text-wutn">Hosted</span></>} />
    </div>
  );
}
